import axios from 'axios'
import Swal from 'sweetalert2'
import { AUTH_API_BASE, PUBLIC_API_BASE, disconnectSocket } from '../utilies/config'

const csrfStates = new Map()
const unsafeMethods = ['post', 'put', 'patch', 'delete']
const csrfTtlMs = 30 * 60 * 1000
const csrfExpiredMessage = 'Sessiya müddəti bitib. Zəhmət olmasa səhifəni yeniləyib yenidən cəhd edin.'
const isCsrfError = (response) =>
    response?.status === 403 &&
    (
        response?.data?.code === 'CSRF_INVALID' ||
        response?.data?.error === 'Invalid CSRF token'
    )
const getCsrfState = (baseURL) => {
    if (!csrfStates.has(baseURL)) {
        csrfStates.set(baseURL, { token: null, promise: null, fetchedAt: 0 })
    }
    return csrfStates.get(baseURL)
}

const getCsrfToken = async (baseURL = PUBLIC_API_BASE) => {
    const state = getCsrfState(baseURL)
    if (state.token && Date.now() - state.fetchedAt < csrfTtlMs) return state.token
    if (state.promise) return state.promise

    const csrfClient = axios.create({ baseURL, withCredentials: true })
    state.promise = csrfClient.get('/csrf-token')
        .then(({ data }) => {
            state.token = data.csrfToken
            state.fetchedAt = Date.now()
            return state.token
        })
        .finally(() => {
            state.promise = null
        })

    return state.promise
}

const clearCsrfToken = (baseURL = PUBLIC_API_BASE) => {
    const state = getCsrfState(baseURL)
    state.token = null
    state.fetchedAt = 0
}

const attachCsrfToken = async (config) => {
    const method = (config.method || 'get').toLowerCase()
    if (!unsafeMethods.includes(method) || config.url?.includes('/csrf-token')) return config

    const token = await getCsrfToken(config.baseURL || PUBLIC_API_BASE)
    config.headers = {
        ...(config.headers || {}),
        'X-CSRF-Token': token
    }
    return config
}

axios.defaults.withCredentials = true
axios.interceptors.request.use(attachCsrfToken)

const api = axios.create({
    baseURL: PUBLIC_API_BASE,
    withCredentials: true,
    headers: {
        'X-Client-App': 'customer'
    }
})

export const authApi = axios.create({
    baseURL: AUTH_API_BASE,
    withCredentials: true,
    timeout: 20000,
    headers: {
        'X-Client-App': 'customer'
    }
})

api.interceptors.request.use(attachCsrfToken)
authApi.interceptors.request.use(attachCsrfToken)

const attachResponseInterceptor = (client) => client.interceptors.response.use(
    (response) => response,
    async (error) => {
        const { response, config } = error
        const method = (config?.method || 'get').toLowerCase()

        if (
            unsafeMethods.includes(method) &&
            isCsrfError(response) &&
            !config._csrfRetry
        ) {
            const baseURL = config.baseURL || PUBLIC_API_BASE
            clearCsrfToken(baseURL)
            config._csrfRetry = true
            const token = await getCsrfToken(baseURL)
            config.headers = {
                ...(config.headers || {}),
                'X-CSRF-Token': token
            }
            return client(config)
        }

        if (unsafeMethods.includes(method) && isCsrfError(response)) {
            response.data = {
                ...(response.data || {}),
                message: csrfExpiredMessage,
                error: csrfExpiredMessage
            }
        }

        const isAuthEndpoint =
            config?.url?.includes('/customer-login') ||
            config?.url?.includes('/customer-register')

        if (!isAuthEndpoint && response?.status === 401) {
            disconnectSocket()
        }

        if (
            !isAuthEndpoint &&
            response?.status === 403 &&
            response?.data?.isBanned
        ) {
            Swal.fire({
                icon: 'error',
                title: 'Hesabınız bloklanıb 🚫',
                html: `
                    <div style="text-align:left">
                        <p><strong>Səbəb:</strong> ${response.data.reason || 'Səbəb yoxdur'}</p>
                        <p><strong>Tarix:</strong> ${new Date(response.data.bannedAt).toLocaleDateString('az-AZ')}</p>
                    </div>
                `,
                confirmButtonText: 'Anladım'
            }).then(() => {
                window.location.href = '/login'
            })
        }

        return Promise.reject(error)
    }
)

attachResponseInterceptor(api)
attachResponseInterceptor(authApi)

export default api
